import * as React from 'react';
import { useInView } from 'react-intersection-observer';

import { useQuery } from '@apollo/client';
import { map } from 'lodash';

import { IArticle } from '@notino/react-styleguide';
import {
  GetBlogArticlesQuery,
  GetBlogArticlesQueryVariables,
} from '@notino/shared/definitions/types';

import messages from '@containers/ProductDetailContainer/messages';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import loadable from '@loadable/component';

import { SectionContainer } from '../SectionContainer';

import { Margin } from './index.styles';
import getBlogArticlesQuery from './queries/getBlogArticles.graphql';
import { useDiorArticles } from './useDiorArticles';

const LoadableArticlesSlider = loadable(
  () => import('./LoadableArticlesSlider')
);

export const BlogArticles = () => {
  const { getTimeFromInit } = useTrackingContext();
  const { product } = useProductDetailContext();
  const diorArticles = useDiorArticles();

  const { kinds, types, categories, subCategories } = product.categorization;
  const brandId = product.brand?.id;
  const masterProductCode = product.masterProductCode;
  const productCodes = product.variants
    .map((v) => v.productCode)
    .filter(Boolean);
  const navigationValues = Array.from(
    new Set(product.variants.flatMap((v) => v.navigationValues || []))
  );

  const [ref, isInView] = useInView({
    triggerOnce: true,
    rootMargin: '50px',
  });

  const { data, error } = useQuery<
    GetBlogArticlesQuery,
    GetBlogArticlesQueryVariables
  >(getBlogArticlesQuery, {
    skip: !isInView || !!diorArticles,
    variables: {
      variables: {
        kinds: map(kinds, 'id'),
        types: map(types, 'id'),
        categories: map(categories, 'id'),
        subCategories: map(subCategories, 'id'),
        brandId: brandId ? Number(brandId) : null,
        masterProductCode,
        productCodes,
        navigationValues,
      },
    },
  });

  const trackClick = (article: IArticle) => {
    dispatchTrackingEvent({
      event: 'article_click',
      _clear: true,
      blog: {
        timing: getTimeFromInit(),
        name: 'blog_tile',
        action: 'click_on_blog_tile',
        interaction: 'click',
        type: 'blog_tile',
        link_path: article.url,
        unified_link:
          articles.find((x) => x.url === article.url)?.hreflangId ?? undefined,
        location: 'product_detail',
        position: `${articles.map((a) => a.url).indexOf(article.url) + 1}`,
      },
    });
  };

  const articles = diorArticles || data?.getArticles?.articles || [];

  return (
    <div ref={ref}>
      {!error && articles.length > 0 && (
        <SectionContainer
          titleMessage={
            diorArticles ? messages.diorDiscoverMore : messages.getInspired
          }
          titleId="pdBlogArticles"
          containerId="pd-blog-articles-header"
          withDivider={false}
        >
          <Margin>
            <LoadableArticlesSlider
              articles={
                articles.slice(0, 3).map((a) => ({
                  ...a,
                  image: { alt: a.image?.alt ?? '', src: a.image?.url ?? '' },
                  titleDataTestId: 'blog-article-title',
                })) as IArticle[]
              }
              onClick={(_, article) => trackClick(article)}
            />
          </Margin>
        </SectionContainer>
      )}
    </div>
  );
};
